import type { Renderer2, RendererFactory2 } from '@angular/core';

import type { OverlayRef, ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import type { ModalOptions } from './modal-options';

/**
 * Strategy that will allow modal to scroll with a page when modal extends past view port
 */
export class ModalPageScrollStrategy implements ScrollStrategy {
  private static readonly MODAL_OPEN_CLASS: string = 'td-wb-modal-open';
  private static readonly PAGE_SCROLL_CLASS: string = 'td-wb-modal-page-scroll';
  private static modalCount: number = 0;
  private static pageScrollCount: number = 0;
  private static readonly sizeCounts: Map<string, number> = new Map<string, number>();

  private renderer: Renderer2;
  private isEnabled: boolean = false;
  private readonly blockStrategy: ScrollStrategy;
  private scrollPosition: number = 0;
  private overlayRef: OverlayRef;
  private resizeObserver: ResizeObserver | null = null;
  private lastIsTall: boolean | null = null;
  private lastRequiredHeight: number = 0;
  private readonly onWindowResize: () => void = (): void => this.updateScrollState();

  constructor(private document: Document,
              private window: Window,
              rendererFactory: RendererFactory2,
              public modalOptions: ModalOptions,
              scrollStrategyOptions: ScrollStrategyOptions
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.blockStrategy = scrollStrategyOptions.block();
  }

  attach(overlayRef: OverlayRef): void {
    this.overlayRef = overlayRef;
    if (this.blockStrategy !== undefined && typeof this.blockStrategy.attach === 'function') {
      this.blockStrategy.attach(overlayRef);
    }
  }

  enable(): void {
    if (this.isEnabled) {
      return;
    }
    this.isEnabled = true;

    this.scrollPosition = this.window.scrollY || this.document.documentElement.scrollTop;

    if (ModalPageScrollStrategy.modalCount === 0) {
      this.renderer.addClass(this.document.body, ModalPageScrollStrategy.MODAL_OPEN_CLASS);
    }
    ModalPageScrollStrategy.modalCount++;

    const sizeClass = `${ModalPageScrollStrategy.MODAL_OPEN_CLASS}--${this.modalOptions.size}`;
    const sizeCount = ModalPageScrollStrategy.sizeCounts.get(this.modalOptions.size) || 0;
    if (sizeCount === 0) {
      this.renderer.addClass(this.document.body, sizeClass);
    }
    ModalPageScrollStrategy.sizeCounts.set(this.modalOptions.size, sizeCount + 1);

    this.updateScrollState();

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.updateScrollState());
      this.resizeObserver.observe(this.overlayRef.overlayElement);
    }
    this.window.addEventListener('resize', this.onWindowResize);
  }

  disable(): void {
    if (!this.isEnabled) {
      return;
    }
    this.isEnabled = false;

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.window.removeEventListener('resize', this.onWindowResize);

    this.setPageScrollMode(false);

    ModalPageScrollStrategy.modalCount--;
    if (ModalPageScrollStrategy.modalCount === 0) {
      this.renderer.removeClass(this.document.body, ModalPageScrollStrategy.MODAL_OPEN_CLASS);
    }

    const sizeClass = `${ModalPageScrollStrategy.MODAL_OPEN_CLASS}--${this.modalOptions.size}`;
    const sizeCount = ModalPageScrollStrategy.sizeCounts.get(this.modalOptions.size) || 0;
    if (sizeCount === 1) {
      this.renderer.removeClass(this.document.body, sizeClass);
      ModalPageScrollStrategy.sizeCounts.delete(this.modalOptions.size);
    } else if (sizeCount > 1) {
      ModalPageScrollStrategy.sizeCounts.set(this.modalOptions.size, sizeCount - 1);
    }

    // Only call blockStrategy.disable() if no other modals are using it (block mode)
    const blockModeCount = ModalPageScrollStrategy.modalCount - ModalPageScrollStrategy.pageScrollCount;
    if (this.blockStrategy !== undefined && blockModeCount === 0) {
      this.blockStrategy.disable();
    }

    // Only restore scroll position if no other modals are in page scroll mode
    if (ModalPageScrollStrategy.pageScrollCount === 0) {
      // Restore prior scroll position using spec-compliant option.
      this.window.scrollTo({ left: 0, top: this.scrollPosition, behavior: 'auto' });
    } else {
      // If other modals are still in page scroll mode, ensure scroll block classes are removed
      this.renderer.removeClass(this.document.body, 'cdk-global-scrollblock');
      this.renderer.removeClass(this.document.documentElement, 'cdk-global-scrollblock');

      // Trigger a resize event to force other modals to recalculate their scroll state
      this.window.dispatchEvent(new Event('resize'));
    }
  }

  private updateScrollState(): void {
    const isXS = this.modalOptions.size === 'extra-small';
    const modalElement = this.overlayRef?.overlayElement;

    // If this is an extra-small modal and page scroll is already active from another modal,
    // don't interfere with the existing page scroll state
    if (isXS && ModalPageScrollStrategy.pageScrollCount > 0) {
      // Set lastIsTall to false so disable() knows this modal is not in tall mode
      this.lastIsTall = false;
      return;
    }

    if (isXS || !modalElement) {
      this.setPageScrollMode(false);
      return;
    }

    const viewportHeight = this.window.innerHeight;
    const modalHeight = modalElement.getBoundingClientRect().height;
    const requiredHeight = modalHeight + 60;

    const buffer = 20;
    const isTall = this.lastIsTall
      ? requiredHeight > viewportHeight - buffer
      : requiredHeight > viewportHeight;

    this.setPageScrollMode(isTall, requiredHeight);
  }

  private setPageScrollMode(enabled: boolean, requiredHeight: number = 0): void {
    const heightChanged: boolean = Math.abs(requiredHeight - this.lastRequiredHeight) >= 5;
    if (enabled === this.lastIsTall && (!enabled || !heightChanged)) {
      return;
    }

    const wasEnabled: boolean | null = this.lastIsTall;
    this.lastIsTall = enabled;
    this.lastRequiredHeight = requiredHeight;

    if (enabled) {
      this.enterPageScrollMode(wasEnabled, requiredHeight);
    } else if (wasEnabled) {
      this.exitPageScrollMode();
    }
  }

  private enterPageScrollMode(wasEnabled: boolean | null, requiredHeight: number): void {
    const previouslyFocusedElement: Element | null = this.document.activeElement;

    if (!wasEnabled) {
      ModalPageScrollStrategy.pageScrollCount++;
      if (ModalPageScrollStrategy.pageScrollCount === 1) {
        this.applyGlobalPageScrollStyles();
      }
    }

    this.applyLocalPageScrollStyles(requiredHeight);
    this.scrollIfRequired(previouslyFocusedElement);
  }

  private exitPageScrollMode(): void {
    this.removeLocalPageScrollStyles();
    ModalPageScrollStrategy.pageScrollCount--;

    if (ModalPageScrollStrategy.pageScrollCount === 0) {
      this.removeGlobalPageScrollStyles();
    }
  }

  private applyGlobalPageScrollStyles(): void {
    const { body, documentElement }: { body: HTMLElement; documentElement: HTMLElement } = this.document;
    this.renderer.addClass(body, ModalPageScrollStrategy.PAGE_SCROLL_CLASS);
    this.renderer.addClass(documentElement, ModalPageScrollStrategy.PAGE_SCROLL_CLASS);

    this.renderer.setStyle(body, 'height', 'auto');
    this.renderer.setStyle(documentElement, 'height', 'auto');

    this.window.scrollTo({ left: 0, top: 0, behavior: 'auto' });

    if (this.blockStrategy) {
      this.blockStrategy.disable();
    }
    this.renderer.removeClass(body, 'cdk-global-scrollblock');
    this.renderer.removeClass(documentElement, 'cdk-global-scrollblock');
  }

  private removeGlobalPageScrollStyles(): void {
    const { body, documentElement }: { body: HTMLElement; documentElement: HTMLElement } = this.document;
    this.renderer.removeClass(body, ModalPageScrollStrategy.PAGE_SCROLL_CLASS);
    this.renderer.removeClass(documentElement, ModalPageScrollStrategy.PAGE_SCROLL_CLASS);
    this.renderer.removeStyle(body, 'min-height');
    this.renderer.removeStyle(body, 'height');
    this.renderer.removeStyle(documentElement, 'height');

    if (this.blockStrategy) {
      this.blockStrategy.enable();
    }
  }

  private applyLocalPageScrollStyles(requiredHeight: number): void {
    const minHeight = `${requiredHeight + 20}px`;
    this.renderer.setStyle(this.document.body, 'min-height', minHeight);

    const backdrop: HTMLElement | null = this.overlayRef?.backdropElement;
    if (backdrop) {
      this.renderer.addClass(backdrop, 'td-wb-modal-page-scroll-item');
      this.renderer.setStyle(backdrop, 'min-height', `${this.document.body.offsetHeight}px`);
    }

    const wrapper: HTMLElement | null = this.overlayRef?.overlayElement?.parentElement;
    if (wrapper) {
      this.renderer.addClass(wrapper, 'td-wb-modal-page-scroll-item');
      this.renderer.setStyle(wrapper, 'min-height', minHeight);
    }
  }

  private removeLocalPageScrollStyles(): void {
    const backdrop: HTMLElement | null = this.overlayRef?.backdropElement;
    if (backdrop) {
      this.renderer.removeClass(backdrop, 'td-wb-modal-page-scroll-item');
      this.renderer.removeStyle(backdrop, 'min-height');
    }

    const wrapper: HTMLElement | null = this.overlayRef?.overlayElement?.parentElement;
    if (wrapper) {
      this.renderer.removeClass(wrapper, 'td-wb-modal-page-scroll-item');
      this.renderer.removeStyle(wrapper, 'min-height');
    }
  }

  private scrollIfRequired(previouslyFocusedElement: Element | null): void {
    if (previouslyFocusedElement && previouslyFocusedElement instanceof HTMLElement) {
      const currentScrollY = this.window.scrollY || this.document.documentElement.scrollTop || 0;
      const elementTopAbsolute = previouslyFocusedElement.getBoundingClientRect().top + currentScrollY;
      const isBodyElement = previouslyFocusedElement === this.document.body;

      // Only scroll if the previously focused element is above the current scroll position.
      if (elementTopAbsolute < currentScrollY || isBodyElement) {
        const block = isBodyElement ? 'start' : 'center';
        previouslyFocusedElement.scrollIntoView({ behavior: 'smooth', block });
      }
    }
  }
}
